import { CheckCircle2, Calendar, User, MessageCircle } from 'lucide-react';
import { useAuth } from '@aaas/shared';
import {
  useWorkspaceSlackIntegrationQuery,
  useWorkspaceSlackOAuthUrl,
} from '../hooks/useIntegrationQuery';
import { useNavigate } from 'react-router-dom';
import type { SlackIntegration } from '@aaas/shared';
import { openSlackOAuthPopup } from '../utils/slackOAuth';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Slack icon component
const SlackIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

const IntegrationsPage = () => {
  const { user, currentWorkspace, idToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [oauthError, setOauthError] = useState<string | null>(null);

  const workspaceOAuthUrlMutation = useWorkspaceSlackOAuthUrl();

  // Fetch workspace-level Slack integration
  const { data: integrationData, isLoading } = useWorkspaceSlackIntegrationQuery({
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!idToken,
  });

  const slackIntegration = (integrationData?.slack?.[0] || null) as SlackIntegration | null;

  const handleSlackCardClick = () => {
    if (slackIntegration) {
      navigate(`/kms/integrations/slack/${slackIntegration.id}`);
    }
  };

  const handleAddSlack = async () => {
    if (!idToken || !currentWorkspace?.id) {
      setOauthError('Authentication required');
      return;
    }

    setOauthError(null);

    try {
      const response = await workspaceOAuthUrlMutation.mutateAsync({
        idToken,
        workspaceId: currentWorkspace.id,
      });

      openSlackOAuthPopup(response.oauth_url, (result) => {
        if (result.success) {
          queryClient.invalidateQueries({ queryKey: ['kms-workspace-integrations', 'slack'] });
        } else if (result.error) {
          setOauthError(result.error);
        }
      });
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to initiate OAuth flow';
      setOauthError(errorMessage);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Integrations</h1>
        <p className="text-gray-600">
          Connect your workspace to external platforms to enable team collaboration features.
        </p>
      </div>

      {/* Error Display */}
      {oauthError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {oauthError}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        /* Integration Cards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Slack Card */}
          {slackIntegration ? (
            // Connected state
            <div
              onClick={handleSlackCardClick}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:border-purple-500 hover:shadow-lg cursor-pointer group"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200">
                    <SlackIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold group-hover:text-purple-600">Slack</h3>
                    <p className="text-sm text-gray-500">{slackIntegration.team_name}</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Connected
                </span>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-4 h-4" />
                  <span className="truncate">{slackIntegration.admin_email || 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(slackIntegration.connected_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">Click to manage integration</p>
              </div>
            </div>
          ) : (
            // Not connected state
            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-6 hover:border-purple-400">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <SlackIcon className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-700">Slack</h3>
                  <p className="text-sm text-gray-500">Not connected</p>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4">
                Enable your team to ask questions about documents directly from Slack.
              </p>

              {/* Connect Button */}
              <button
                onClick={handleAddSlack}
                disabled={workspaceOAuthUrlMutation.isPending}
                className="w-full cursor-pointer transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center"
              >
                <img
                  alt="Add to Slack"
                  height="40"
                  width="139"
                  src="https://platform.slack-edge.com/img/add_to_slack.png"
                  srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
                />
              </button>
            </div>
          )}

          {/* Discord Card - Coming Soon */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 opacity-60 cursor-not-allowed relative">
            {/* Coming Soon Badge */}
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                Coming Soon
              </span>
            </div>

            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <MessageCircle className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-400">Discord</h3>
                <p className="text-sm text-gray-400">Not available</p>
              </div>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-400 mb-4">
              Connect your Discord server to enable document Q&A through Discord channels.
            </p>

            {/* Disabled Button */}
            <button
              disabled
              className="w-full py-2 px-4 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed"
            >
              Connect Discord
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
